'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { CSVExportManager, getCSVExportManager } from '~/utils/csv-export/export-manager'
import { ExportWorkerFactory } from '~/utils/csv-export/export-worker'
import type {
  CSVExportContextValue,
  ExportConfig,
  ExportEvent,
  ExportHistoryRecord,
  ExportRecord,
  StorageUsage,
} from '~/utils/csv-export/types'
import { CSVExportContext } from './csv-export-context'

interface CSVExportProviderProps {
  children: React.ReactNode
  /**
   * User ID for filtering exports
   */
  userId?: string
  /**
   * Auto-cleanup interval in minutes (default: 30)
   */
  cleanupInterval?: number
  /**
   * Maximum number of concurrent exports (default: 3)
   */
  maxConcurrentExports?: number
}

export const CSVExportProvider: React.FC<CSVExportProviderProps> = ({
  children,
  userId,
  cleanupInterval = 30,
  maxConcurrentExports = 3,
}) => {
  const [activeExports, setActiveExports] = useState<ExportRecord[]>([])
  const [exportHistory, setExportHistory] = useState<ExportHistoryRecord[]>([])
  const [storageUsage, setStorageUsage] = useState<StorageUsage>({ used: 0, available: 0, percentage: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [manager, setManager] = useState<CSVExportManager | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const initializeManager = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const exportManager = getCSVExportManager()
        await exportManager.initializeDB()

        setManager(exportManager)

        await exportManager.resumeActiveExports()

        await Promise.all([
          loadActiveExports(exportManager),
          loadExportHistory(exportManager),
          loadStorageUsage(exportManager),
        ])
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize CSV Export Provider'

        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    initializeManager()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!manager) return

    const unsubscribe = manager.subscribe((event: ExportEvent) => {
      handleExportEvent(event)
    })

    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager])

  useEffect(() => {
    if (!manager || cleanupInterval <= 0) return

    const interval = setInterval(
      async () => {
        try {
          await manager.cleanupExpiredExports()
          await loadStorageUsage(manager)
        } catch {
          // Silently handle cleanup errors
        }
      },
      cleanupInterval * 60 * 1000,
    )

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager, cleanupInterval])

  const loadActiveExports = useCallback(
    async (exportManager: CSVExportManager) => {
      try {
        const exports = await exportManager.getActiveExports(userId)
        setActiveExports(exports)
      } catch {
        // Silently handle load errors
      }
    },
    [userId],
  )

  const loadExportHistory = useCallback(
    async (exportManager: CSVExportManager) => {
      try {
        const history = await exportManager.getExportHistory(userId)
        setExportHistory(history)
      } catch {
        // Silently handle load errors
      }
    },
    [userId],
  )

  const loadStorageUsage = useCallback(async (exportManager: CSVExportManager) => {
    try {
      const usage = await exportManager.getStorageUsage()
      setStorageUsage(usage)
    } catch {
      // Silently handle storage errors
    }
  }, [])

  const handleExportEvent = useCallback(
    async (event: ExportEvent) => {
      if (!manager) return

      switch (event.type) {
        case 'created':
        case 'progress':
          await loadActiveExports(manager)
          break

        case 'completed':
          await Promise.all([loadActiveExports(manager), loadExportHistory(manager), loadStorageUsage(manager)])
          break

        case 'error':
        case 'cancelled':
          await loadActiveExports(manager)
          break
      }
    },
    [manager, loadActiveExports, loadExportHistory, loadStorageUsage],
  )

  const startExport = useCallback(
    async (config: ExportConfig): Promise<string> => {
      if (!manager) {
        throw new Error('Export manager not initialized')
      }

      if (!userId) {
        throw new Error('User ID is required to start export')
      }

      const currentActiveCount = activeExports.filter((exp) =>
        ['queued', 'initializing', 'fetching', 'processing'].includes(exp.status.state),
      ).length

      if (currentActiveCount >= maxConcurrentExports) {
        throw new Error(`Maximum ${maxConcurrentExports} concurrent exports allowed`)
      }

      const exportId = await manager.createExport(config, userId)

      if (!config.queryHook || (!config.queryHook.lazyQuery && !config.queryHook.result)) {
        throw new Error('Config must include a queryHook with either lazyQuery or result')
      }

      const { queryHook } = config

      await ExportWorkerFactory.startExport(exportId, config, manager, queryHook)

      return exportId
    },
    [manager, userId, activeExports, maxConcurrentExports],
  )

  const cancelExport = useCallback(
    async (exportId: string): Promise<void> => {
      if (!manager) {
        throw new Error('Export manager not initialized')
      }

      await ExportWorkerFactory.cancelExport(exportId)
      await manager.cancelExport(exportId)

      const exports = await manager.getActiveExports(userId)
      exports.find((exp) => exp.id === exportId)
    },
    [manager, userId],
  )

  const dismissExport = useCallback(
    async (exportId: string): Promise<void> => {
      if (!manager) {
        throw new Error('Export manager not initialized')
      }

      await manager.dismissExport(exportId)
    },
    [manager],
  )

  const pauseExport = useCallback(async (exportId: string): Promise<void> => {
    throw new Error('Pause export functionality not implemented')
  }, [])

  const resumeExport = useCallback(async (exportId: string): Promise<void> => {
    throw new Error('Resume export functionality not implemented')
  }, [])

  const downloadExport = useCallback(
    async (exportId: string): Promise<void> => {
      if (!manager) {
        throw new Error('Export manager not initialized')
      }

      const activeExport = activeExports.find((exp) => exp.id === exportId)

      if (activeExport && activeExport.downloadUrl) {
        const link = document.createElement('a')
        link.href = activeExport.downloadUrl
        link.download = activeExport.config.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return
      }

      const historyRecord = exportHistory.find((h) => h.id === exportId)
      if (!historyRecord) {
        throw new Error('Export not found in active exports or history')
      }

      if (!historyRecord.downloadUrl) {
        throw new Error('Download URL not available')
      }

      const link = document.createElement('a')
      link.href = historyRecord.downloadUrl
      link.download = historyRecord.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      await manager.markAsDownloaded(exportId)
      await loadExportHistory(manager)
    },
    [manager, activeExports, exportHistory, loadExportHistory],
  )

  const clearHistory = useCallback(async (): Promise<void> => {
    if (!manager) {
      throw new Error('Export manager not initialized')
    }

    throw new Error('Clear history functionality not implemented')
  }, [manager])

  const optimizeStorage = useCallback(async (): Promise<void> => {
    if (!manager) {
      throw new Error('Export manager not initialized')
    }

    await manager.optimizeStorage()
    await loadStorageUsage(manager)
  }, [manager, loadStorageUsage])

  const contextValue: CSVExportContextValue = {
    activeExports,

    startExport,
    cancelExport,
    dismissExport,
    pauseExport,
    resumeExport,

    exportHistory,
    downloadExport,
    clearHistory,

    isLoading,
    error,

    storageUsage,
    optimizeStorage,
  }

  return <CSVExportContext.Provider value={contextValue}>{children}</CSVExportContext.Provider>
}

export default CSVExportProvider
